/** Technicals tab. The score is broken into its inputs so it is inspectable
 * rather than a single opaque number. Mock data only.
 */
import { useMemo, useState } from "react";
import {
  CachedBadge, ContributionBar, DataNotice, KeyRow, MockBadge, PanelGrid, SectionTitle, StatTile, TileRow,
  WorkspacePanel,
} from "@/workspace/primitives";
import {
  CandleChart, HistogramChart, MultiLineChart, OscillatorChart, VolumeChart,
} from "@/workspace/charts";
import { TechnicalEmaDetail } from "@/workspace/TechnicalEntryContext";
import { keyLevels, pinWeeklyEmaForEntry, technicalInputs, weeklyBars } from "@/workspace/mockTickerData";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";

export function TechnicalsTab({ t }) {
  const [emaMode, setEmaMode] = useState("daily");
  const bars = useMemo(() => (t.series || []).slice(-120), [t.series]);
  const weekly = useMemo(
    () => (t.live ? [] : pinWeeklyEmaForEntry(t.ticker, weeklyBars(t.series)).slice(-52)),
    [t.series, t.ticker, t.live],
  );
  const inputs = t.live ? [] : technicalInputs(t.ticker);
  const levels = t.live ? (t.keyLevels || { support: [], resistance: [] }) : keyLevels(t.ticker);
  const last = bars[bars.length - 1];
  const adx = t.live
    ? (t.signalsLive?.adx14 != null ? String(t.signalsLive.adx14) : null)
    : inputs.find((i) => i.input.startsWith("ADX"))?.reading;
  const notice = t.live ? <CachedBadge /> : <MockBadge />;
  const liveSig = t.signalsLive || {};
  const extraLines = emaMode === "daily"
    ? [
      { key: "ema9", color: "#E879F9", label: "EMA9" },
      { key: "ema20", color: "#22D3EE", label: "EMA20", dash: "4 3" },
    ]
    : [];

  return (
    <div className="space-y-3">
      <TileRow cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Close" value={last?.close != null ? last.close.toFixed(2) : "—"} testid="tech-tile-close" />
        <StatTile label="SMA20" value={t.live ? "—" : (last?.sma20?.toFixed(2) ?? "—")} sub={t.live ? LIVE_UNAVAILABLE.smaSeries : undefined} />
        <StatTile label="SMA50" value={(t.live ? liveSig.sma50 : last?.sma50)?.toFixed?.(2) ?? (t.live && liveSig.sma50 == null ? "—" : last?.sma50?.toFixed(2) ?? "—")} />
        <StatTile
          label="SMA200"
          value={(t.live ? liveSig.sma200 : last?.sma200)?.toFixed?.(2) ?? "—"}
          sub={t.live && liveSig.sma200 == null ? LIVE_UNAVAILABLE.smaSeries : undefined}
        />
        <StatTile label="RSI (14)" value={t.live ? (liveSig.rsi14 != null ? liveSig.rsi14.toFixed(1) : "—") : (last?.rsi14?.toFixed(1) ?? "—")} />
        <StatTile label="ADX (14)" value={adx || "—"} />
        {t.live ? <StatTile label="ATR" value="—" sub={LIVE_UNAVAILABLE.atr} /> : null}
      </TileRow>

      <WorkspacePanel
        title="Candles, moving averages and bands"
        subtitle="Last 120 sessions · SMA 20/50/200 retained · EMA 9/20 as a supplement on Daily"
        notice={notice}
        hint={t.live ? "Cached daily bars. EMA overlays are not drawn unless those series exist on the payload." : "Fixture OHLCV. Production reads the cached daily bars and the compact technicals snapshot."}
        testid="tech-candles"
      >
        {bars.length ? (
          <CandleChart series={bars} height={300} levels={levels} extraLines={t.live ? [] : extraLines} smaLegend={t.live ? [] : undefined} testid="tech-candle-chart" />
        ) : (
          <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>OHLCV unavailable from current cached data.</p>
        )}
        <div className="mt-2">
          <SectionTitle>Volume</SectionTitle>
          <VolumeChart series={bars} height={72} testid="tech-volume-chart" />
        </div>
      </WorkspacePanel>

      <WorkspacePanel
        title="EMA relationships"
        subtitle="Daily 9/20 or weekly 10/20 — analytical context only"
        notice={notice}
        testid="tech-ema-panel"
        actions={(
          <div className="flex gap-1" data-testid="ema-mode-toggle">
            {["daily", "weekly"].map((m) => {
              const active = emaMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setEmaMode(m)}
                  data-testid={`ema-mode-${m}`}
                  className="text-[10px] px-2 py-0.5 rounded-sm border capitalize"
                  style={{
                    color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                    background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                    borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
                  }}
                >
                  {m}
                </button>
              );
            })}
          </div>
        )}
      >
        {t.live ? (
          <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }} data-testid="tech-ema-unavailable">
            {emaMode === "weekly" ? LIVE_UNAVAILABLE.emaWeekly : LIVE_UNAVAILABLE.emaDaily}
          </p>
        ) : emaMode === "weekly" ? (
          <div className="mb-2">
            <CandleChart
              series={weekly}
              height={180}
              extraLines={[
                { key: "ema10", color: "#E879F9", label: "EMA10" },
                { key: "ema20", color: "#22D3EE", label: "EMA20", dash: "4 3" },
              ]}
              testid="tech-weekly-candle-chart"
            />
            <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
              Weekly bars are a fixture aggregation of the daily mock series. Daily SMA 20/50/200 stay on the main candle chart.
            </p>
          </div>
        ) : null}
        {t.live ? null : <TechnicalEmaDetail ticker={t} mode={emaMode} />}
      </WorkspacePanel>

      <PanelGrid>
        <WorkspacePanel title="RSI (14)" notice={notice} testid="tech-rsi">
          {t.live ? (
            <div>
              <KeyRow label="Current RSI (14)">{liveSig.rsi14 != null ? liveSig.rsi14.toFixed(1) : "—"}</KeyRow>
              <p className="text-[11px] mt-2" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.rsiSeries}</p>
            </div>
          ) : (
            <OscillatorChart series={bars} dataKey="rsi14" bands={[30, 70]} domain={[0, 100]} height={120} testid="tech-rsi-chart" />
          )}
        </WorkspacePanel>
        <WorkspacePanel title="MACD histogram" notice={notice} testid="tech-macd">
          {t.live ? (
            <div>
              <KeyRow label="Current MACD histogram">{liveSig.macdHistogram != null ? liveSig.macdHistogram.toFixed(4) : "—"}</KeyRow>
              <p className="text-[11px] mt-2" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.macdSeries}</p>
            </div>
          ) : (
            <HistogramChart series={bars} dataKey="macdHistogram" height={120} testid="tech-macd-chart" />
          )}
        </WorkspacePanel>
      </PanelGrid>

      <PanelGrid>
        <WorkspacePanel
          title="Technical score inputs"
          subtitle={`Score ${t.technicalScore != null ? t.technicalScore.toFixed(1) : "—"} of 10 · each input's direction and strength, not addends`}
          notice={notice}
          hint="Readings are derived from the same bars the charts draw. Contribution weights are illustrative, not the production scoring function."
          testid="tech-inputs"
        >
          <div className="space-y-1">
            {t.live ? (
              <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>
                Contribution weights are not available from current cached data. Current snapshot: SMA50 {liveSig.sma50 ?? "—"} · SMA200 {liveSig.sma200 ?? "—"} · RSI {liveSig.rsi14 ?? "—"} · ADX {liveSig.adx14 ?? "—"} · BB {liveSig.bbLower ?? "—"} / {liveSig.bbUpper ?? "—"}.
              </p>
            ) : null}
            {inputs.map((i) => (
              <div key={i.input} className="flex items-center gap-2 text-[10px] py-0.5">
                <span className="w-[38%] shrink-0 truncate" style={{ color: "var(--hoip-text-secondary)" }}>{i.input}</span>
                <span className="w-[22%] shrink-0 font-mono-data truncate">{i.reading}</span>
                <ContributionBar value={i.contribution} />
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
            <DataNotice adminText="Production computes these in SignalEngine.compute_technical_signals and caches the compact set on scanner_cache.technicals." />
          </div>
        </WorkspacePanel>

        <WorkspacePanel
          title="Relative strength"
          subtitle="Rebased to 100 · ticker vs SPY vs sector"
          notice={notice}
          testid="tech-rs"
        >
          {t.live ? (
            <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }} data-testid="tech-rs-unavailable">
              {LIVE_UNAVAILABLE.relativeStrength}
            </p>
          ) : (
          <MultiLineChart
            data={t.relativeStrength}
            height={190}
            lines={[
              { key: "ticker", label: t.ticker, color: "#E4E4E7" },
              { key: "spy", label: "SPY", color: "#93C5FD", dash: "4 3" },
              { key: "sector", label: "Sector ETF", color: "#A1A1AA", dash: "2 3" },
            ]}
            testid="tech-rs-chart"
          />
          )}
        </WorkspacePanel>
      </PanelGrid>

      <PanelGrid>
        <WorkspacePanel title="Resistance" testid="tech-resistance">
          {levels.resistance?.length
            ? levels.resistance.map((r) => (
              <KeyRow key={`${r.label}-${r.level}`} label={r.label}>
                <span style={{ color: "#EF4444" }}>▼ {Number(r.level).toFixed(2)}</span>
              </KeyRow>
            ))
            : <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>Not available in current cached output</p>}
        </WorkspacePanel>
        <WorkspacePanel title="Support" testid="tech-support">
          {levels.support?.length
            ? levels.support.map((s) => (
              <KeyRow key={`${s.label}-${s.level}`} label={s.label}>
                <span style={{ color: "#10B981" }}>▲ {Number(s.level).toFixed(2)}</span>
              </KeyRow>
            ))
            : <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>Not available in current cached output</p>}
        </WorkspacePanel>
      </PanelGrid>
    </div>
  );
}
